import { ProductIds, ApplicationStatusTypeIds } from "@/config/constants";
import type { AppData } from "@/models/application";
import useApplicationData from "@/composables/application/useApplicationData";

export default function useLeftPanelText(): any {
  function getCurrentText(state: string, appData: AppData): string {
    const withCoApplicants: boolean = appData?.application?.metadata?.withCoApplicants;
    const productTypeId: number = appData?.application?.productTypeId;
    const { isParentLOC } = useApplicationData();

    switch (state) {
      case "chooseBorrowerCoborrower":
      case "dgChooseBorrowerCoborrower":
      case "refiChooseBorrowerCoSigner":
        return "Fund your future with a credit union!";
      case "schoolInformation":
      case "dgSchoolInformation":
        return "College is a big investment - we can help.";
      case "refiSchoolInformation":
        return "Take control of your student loan repayment.";
      case "coborrowerPersonalInfo":
      case "coborrowerInvitedApplicantInfo":
        return "Enter your information and then view estimated rates.";
      case "borrowerPersonalInfo":
      case "borrowerInvitedApplicantInfo":
        if (productTypeId === ProductIds.PEL) {
          return withCoApplicants ? "Enter your information." : "Enter your information and then view estimated rates.";
        }

        if (state === "borrowerPersonalInfo" && isParentLOC.value) {
          return "Flexible funding options to best meet your needs.";
        }

        return "Enter your information and then view estimated rates.";
      case "refiCosignerPersonalInfo":
      case "refiBorrowerPersonalInfo":
      case "refiBorrowerInvitedApplicantInfo":
      case "refiCosignerInvitedApplicantInfo":
        return "Enter your info to view estimated rates.";
      case "coborrowerPersonalInfoContinued":
      case "coborrowerInvitedApplicantInfoContinued":
        return "Enter your information and then view estimated rates.";
      case "borrowerPersonalInfoContinued":
      case "borrowerInvitedApplicantInfoContinued":
        if (productTypeId === ProductIds.PEL) {
          return withCoApplicants ? "Enter your information." : "Enter your information and then view estimated rates.";
        }

        if (state === "borrowerPersonalInfoContinued" && isParentLOC.value) {
          return "Flexible funding options to best meet your needs.";
        }

        return "Enter your information and then view estimated rates.";
      case "refiCosignerPersonalInfoContinued":
      case "refiBorrowerPersonalInfoContinued":
      case "refiBorrowerInvitedApplicantInfoContinued":
      case "refiCosignerInvitedApplicantInfoContinued":
        return "Enter your info to view estimated rates.";
      case "showMyRates":
				if (isParentLOC.value) {
					return "Estimate your rates with no impact to your credit score."
				}
        return "Flexible funding options to best meet your needs.";
      case "refiShowMyRates":
        return "Flexible refinance options to best meet your needs.";
      case "coborrowerRegistration":
        return ""; // handled by CYA component
      case "refiCosignerRegistration":
        return "You're almost there. Set up your account and keep rolling!";
      case "borrowerAdditionalInfo":
      case "dgBorrowerAdditionalInfo":
      case "dgCoborrowerAdditionalInfo":
      case "coborrowerAdditionalInfo":
      case "coborrowerMaritalStatus":
      case "borrowerMaritalStatus":
      case "refiBorrowerMaritalStatus":
      case "refiBorrowerUnderlyingLoansToRefinance":
      case "refiBorrowerAdditionalInfo":
      case "refiCosignerMaritalStatus":
      case "refiCosignerUnderlyingLoansToRefinance":
      case "refiCosignerAdditionalInfo":
        return "Just a quick check of some important information.";
      case "refiCosignerHardPullConsent":
        return "Isn't it nice knowing we're on your side?";
      case "borrowerInfo":
      case "borrowerInfoContinued":
      case "refiBorrowerInfo":
      case "refiBorrowerInfoContinued":
        return "You're helping make their college dream a reality. Thank you.";
      case "borrowerInvited":
      case "refiBorrowerInvited":
        return "It's time to make your college dream a reality.";
      case "coborrowerInvited":
      case "refiCosignerInvited":
        return "You're helping make their college dream a reality. Thank you.";
      case "borrowerRegistration":
        return ""; // handled by CYA component
      case "refiBorrowerRegistration":
        return "Let's set up your account for this application.";
      case "refiBorrowerHardPullConsent":
        return "You're almost there!";
      case "borrowerHardPullConsent":
      case "coborrowerHardPullConsent":
        return "One more click to submit!";
      case "addCoborrower":
      case "dgAddCoborrower":
        if (productTypeId === ProductIds.PEL) {
          return "Adding a cosigner may help you secure a better rate.";
        } else {
          return "Adding a co-borrower may help you secure a better rate.";
        }
      case "refiAddCosigner":
        return "Adding a cosigner may help you secure a better rate.";
      case "coborrowerInfo":
      case "coborrowerInfoContinued":
        if (productTypeId === ProductIds.PEL) {
          return "Great work. Now let's verify your cosigner's information.";
        } else {
          return "Great work. Now let's verify your co-borrower's information.";
        }
      case "refiCosignerInfo":
      case "refiCosignerInfoContinued":
        return "Great work. Now let's verify your cosigner's information.";
      case "studentInformation":
        return "Fund your student's future with a credit union.";
      case "approval":
      case "dgApproval":
      case "loanConfiguration":
      case "refiApproval":
      case "refiLoanConfiguration":
      case "refiShowMyRatesExit":
      default:
        return "";
    }
  }

  // Placeholder for the REFI routes TBD
  function getCurrentTextByProductId(state: string, productTypeId: number) {
    if (productTypeId === 2) {
      switch (state) {
        case "refiChooseBorrowerCoSigner":
          return "Fund your future with a credit union!";
        case "refiSchoolInformation":
          return "Take control of your student loan repayment.";
        case "refiCosignerPersonalInfo":
          return "Enter your info to view estimated rates.";
        case "refiBorrowerPersonalInfo":
          return "Enter your info to view estimated rates.";
        case "refiCosignerPersonalInfoContinued":
          return "Enter your info to view estimated rates.";
        case "refiBorrowerPersonalInfoContinued":
          return "Enter your info to view estimated rates.";
        case "refiShowMyRates":
          return "Flexible refinance options to best meet your needs.";
        case "refiBorrowerRegistration":
          return "Let's set up your account for this application.";
        case "refiBorrowerMaritalStatus":
          return "Just a quick check of some important information.";
        case "refiBorrowerUnderlyingLoansToRefinance":
          return "Just a quick check of some important information.";
        case "refiBorrowerAdditionalInfo":
          return "Just a quick check of some important information.";
        case "refiAddCosigner":
          return "Adding a cosigner may help you secure a better rate.";
        case "refiBorrowerHardPullConsent":
          return "You're almost there!";
        case "refiCosignerInfo":
          return "Great work. Now let's verify your cosigner's information.";
        case "refiBorrowerInfo":
          return "You're helping make their college dream a reality. Thank you.";
        case "refiBorrowerInvited":
          return "It's time to make your college dream a reality.";
        case "refiBorrowerInvitedApplicantInfo":
          return "Enter your info to view estimated rates.";
        case "refiBorrowerInvitedApplicantInfoContinued":
          return "Enter your info to view estimated rates.";
        case "refiBorrowerInfoContinued":
          return "You're helping make their college dream a reality. Thank you.";
        case "refiApproval":
          return "";
        case "refiLoanConfiguration":
          return "";
        case "refiCosignerInvited":
          return "You're helping make their college dream a reality. Thank you.";
        case "refiCosignerInfoContinued":
          return "Great work. Now let's verify your cosigner's information.";
        case "refiCosignerInvitedApplicantInfo":
          return "Enter your info to view estimated rates.";
        case "refiCosignerInvitedApplicantInfoContinued":
          return "Enter your info to view estimated rates.";
        case "refiCosignerRegistration":
          return "You're almost there. Set up your account and keep rolling!";
        case "refiCosignerMaritalStatus":
          return "Just a quick check of some important information.";
        case "refiCosignerUnderlyingLoansToRefinance":
          return "Just a quick check of some important information.";
        case "refiCosignerAdditionalInfo":
          return "Just a quick check of some important information.";
        case "refiCosignerHardPullConsent":
          return "Isn't it nice knowing we're on your side?";
        case "refiShowMyRatesExit":
          return "";
        default:
          return "";
      }
    }
  }
  function getApprovalStepText(status: number): string {
    switch (status) {
      case ApplicationStatusTypeIds.EVALUATION:
        return "Thanks! We'll handle it from here.";
      case ApplicationStatusTypeIds.DECLINED:
        return "Thank you for giving us the opportunity.";
      default:
        return "Need help with next steps? Let us know.";
    }
  }
  function getLoanConfigurationText(substep: number) {
    switch (substep) {
      case 1:
        return "You're making a smart choice.";
      case 2:
        return "Tell us how much you need.";
      case 3:
        return "Choose wisely and reach out if you have questions.";
      case 4:
        return "Nice! Now go get that degree.";
      default:
        return "";
    }
  }
  function getRefiLoanConfigurationText(substep: number) {
    switch (substep) {
      case 1:
        return "Select your rate and repayment term.";
      default:
        return "You're ready for the next steps. Let's do this!";
    }
  }

  function getRegistrationLeftPanelText(currentRoute: string, authorizedUser: boolean) {
    if (currentRoute === "borrowerRegistration") {
      switch (authorizedUser) {
        case true:
          return "Let's verify some details.";
        default:
          return "Let's set up your account.";
      }
    }

    if (
      currentRoute === "coborrowerRegistration" ||
      currentRoute === "refiBorrowerRegistration" ||
      currentRoute === "refiBorrowerRegistration"
    ) {
      switch (authorizedUser) {
        case true:
          return "Let's verify some details.";
        default:
          return "Let's set up your account for this application.";
      }
    }
  }

  function getMobileCommunicationsLeftPanelText() {
    const product = localStorage.getItem("product");

    if (product === ProductIds.REFI.toString()) {
      return "Let's set up your account for this application.";
    } else {
      return "";
    }
  }

  return {
    getCurrentText,
    getLoanConfigurationText,
    getApprovalStepText,
    getRefiLoanConfigurationText,
    getCurrentTextByProductId,
    getMobileCommunicationsLeftPanelText,
    getRegistrationLeftPanelText,
  };
}
